import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Search, ScrollText, Eye, Archive, Trash2, Printer, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const base = `${API}/api/historico-escolar`;

export default function HistoricoEmissoes() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${base}/emissoes/list`, { params: { q: q || undefined }, headers });
      setItems(r.data || []);
    } catch { toast.error('Falha ao carregar emissões'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const arquivar = async (id) => { await axios.post(`${base}/emissoes/${id}/arquivar`, {}, { headers }); toast.success('Arquivado'); load(); };
  const excluir = async (id) => { if (!confirm('Excluir esta emissão?')) return; await axios.delete(`${base}/emissoes/${id}`, { headers }); toast.success('Excluído'); load(); };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ScrollText className="w-7 h-7 text-purple-700" />Históricos Gerados
          </h1>
          <p className="text-sm text-slate-500">Documentos emitidos · Secretaria DNACore V2</p>
        </div>
        <Button onClick={() => navigate('/v2/secretaria/historico')} className="bg-gradient-to-r from-purple-700 to-emerald-600 text-white" data-testid="hist-novo">
          + Novo Histórico
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-sm text-purple-800">Emissões</CardTitle>
            <div className="relative w-72">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Buscar por aluno, RA ou código" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="hist-emissoes-search" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-6 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Carregando…</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm">Nenhum histórico emitido ainda.</div>
          ) : (
            <div className="overflow-x-auto dna-responsive-table sticky-first-col">
              <table className="w-full text-xs">
                <thead className="bg-purple-50 text-purple-800">
                  <tr>
                    <th className="text-left py-2 px-2">Aluno</th>
                    <th className="text-left py-2 px-2">RA</th>
                    <th className="text-left py-2 px-2">Tipo</th>
                    <th className="text-left py-2 px-2">Situação</th>
                    <th className="text-left py-2 px-2">Código</th>
                    <th className="text-left py-2 px-2">Emitido em</th>
                    <th className="text-right py-2 px-2">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(e => (
                    <tr key={e.id} className={`border-b ${e.arquivado ? 'opacity-50' : ''}`}>
                      <td className="px-2 py-1.5 font-semibold">{e.student_nome}</td>
                      <td className="px-2 py-1.5">{e.student_ra || '—'}</td>
                      <td className="px-2 py-1.5"><Badge variant="outline" className="text-[10px]">{e.fonte === 'manual' ? 'Manual' : 'DNACore'}</Badge></td>
                      <td className="px-2 py-1.5">
                        <Badge className={`text-[10px] ${e.situacao_final === 'Aprovado' || e.situacao_final === 'Concluído' ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'}`}>{e.situacao_final || '—'}</Badge>
                      </td>
                      <td className="px-2 py-1.5 font-mono text-purple-700 text-[10px]">{(e.token || '').toUpperCase()}</td>
                      <td className="px-2 py-1.5">{(e.emitido_em || '').slice(0, 16).replace('T', ' ')}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" className="h-7 text-[10px] mr-1" onClick={() => navigate(`/v2/secretaria/historico/${e.student_id}`)} data-testid={`hist-emissao-view-${e.id}`}>
                          <Eye className="w-3 h-3 mr-1" />Ver
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-[10px] mr-1" onClick={() => window.open(`/v2/secretaria/historico/validar/${e.token}`, '_blank')}>
                          <Printer className="w-3 h-3 mr-1" />Validar
                        </Button>
                        {!e.arquivado && (
                          <Button size="sm" variant="outline" className="h-7 text-[10px] mr-1" onClick={() => arquivar(e.id)}>
                            <Archive className="w-3 h-3 mr-1" />Arquivar
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="h-7 text-[10px] text-rose-600 border-rose-200" onClick={() => excluir(e.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
